import Clear from "@mui/icons-material/Clear";
import FilterList from "@mui/icons-material/FilterList";
import Tune from "@mui/icons-material/Tune";
import { Box, Button, IconButton, Stack, SxProps, TextField, Theme, Tooltip } from "@mui/material";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { useCurrentUser } from "hooks/useCurrentUser";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { bolt } from "utils/metricsLogger";

import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { FilterGroup, FilterPopover, getCanonicalKey } from "./FilterPopover";

export type Filter = {
  omnisearch?: string;
};

export type OmnisearchProps = {
  autoFocus?: boolean;
  columnFilterKeys?: FilterKey[];
  sx?: SxProps;
  /**
   * Opt-in styles merged onto the search input's TextField (e.g. a white
   * background). Undefined by default — leaves the input's appearance untouched.
   */
  searchFieldSx?: SxProps<Theme>;
  omnisearch: string;
  setOmnisearch: (newVal: string) => void;
  /** Immediate update (no debounce) - for clear button, programmatic changes */
  setOmnisearchImmediate?: (newVal: string) => void;
  onApplyFilters?: (newVal: string) => void;
  inputRef?: React.RefObject<HTMLElement | null>;
  resetRef?: React.RefObject<HTMLButtonElement | null>;
  filterGroups?: FilterGroup[];
  /** Show filter button inside the search input (tune icon). Default true. */
  showFilterButtonInside?: boolean;
  /** Show external "Filters" button outside the search input. Default false. */
  showExternalFilterButton?: boolean;
  /**
   * Custom render function for the external filter button.
   * Receives the click handler and returns the button element.
   * If not provided, a default CommandButton-style button is rendered.
   */
  renderFilterButton?: (onClick: (event: MouseEvent<HTMLButtonElement>) => void) => React.ReactNode;
  persistenceKey?: string;
  keyAliases?: Record<string, string>;
  /**
   * Omnisearch keys that are valid in saved filters but are not column filter
   * keys — e.g. page-level toggles like the fulfillment "View by" selector
   * (assignedToMe/needsMyAttention/peerReviewQueue). Without this they'd be
   * stripped by filterValidKeys when a saved filter is restored. Pass a stable
   * (module-level) array reference to avoid re-renders.
   */
  preservedFilterKeys?: string[];
  /** Optional tenantId for backend queries. */
  tenantId?: string;
  /** Opt-in for wide (3-column) filter layout. */
  wideLayout?: boolean;
};

// Stable default sx to prevent re-renders from inline object references
const DEFAULT_SX = { flexGrow: 1 } as const;

// Stable default so the preservedFilterKeys default doesn't create a new array
// reference each render (which would churn filterValidKeys' memoization).
const EMPTY_PRESERVED_FILTER_KEYS: string[] = [];

export function Omnisearch({
  autoFocus = false,
  columnFilterKeys,
  sx = DEFAULT_SX,
  searchFieldSx,
  omnisearch,
  setOmnisearch,
  setOmnisearchImmediate,
  onApplyFilters,
  inputRef,
  resetRef,
  filterGroups,
  showFilterButtonInside = true,
  showExternalFilterButton = false,
  renderFilterButton,
  persistenceKey,
  keyAliases = {},
  preservedFilterKeys = EMPTY_PRESERVED_FILTER_KEYS,
  tenantId,
  wideLayout,
}: OmnisearchProps) {
  const currentUser = useCurrentUser();

  // Filter popover anchor - managed internally to avoid parent re-renders
  const [filterButtonAnchor, setFilterButtonAnchor] = useState<HTMLButtonElement | null>(null);

  // Local input state for responsive TextField - prevents parent re-renders from blocking typing
  const [localInputValue, setLocalInputValue] = useState(omnisearch);
  const isLocalChange = useRef(false);

  // Sync local state when parent's omnisearch changes (e.g., from URL updates, filter applies)
  // Only sync if it wasn't a local change (user typing) to avoid overwriting mid-edit
  useEffect(() => {
    if (omnisearch !== localInputValue) {
      if (!isLocalChange.current) {
        setLocalInputValue(omnisearch);
      }
    }
    // Reset the flag after processing the omnisearch change
    isLocalChange.current = false;
    // oxlint-disable-next-line exhaustive-deps
  }, [omnisearch]); // Intentionally omit localInputValue - we only care when omnisearch prop changes

  // Handle input changes - update local state immediately, notify parent
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      isLocalChange.current = true;
      setLocalInputValue(newValue);
      setOmnisearch(newValue);
    },
    [setOmnisearch]
  );

  // Handle clear - update both local and parent (immediate, no debounce)
  const handleClear = useCallback(() => {
    bolt.startInteraction("omnisearch.clear");
    isLocalChange.current = true;
    setLocalInputValue("");
    if (setOmnisearchImmediate) {
      setOmnisearchImmediate("");
    } else {
      setOmnisearch("");
    }
  }, [setOmnisearch, setOmnisearchImmediate]);

  // Handler for opening filter popover - used by both internal and external buttons
  const handleOpenFilter = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setFilterButtonAnchor(event.currentTarget);
  }, []);

  const handleCloseFilter = useCallback(() => {
    setFilterButtonAnchor(null);
  }, []);

  const onApply = useCallback(
    (newOmnisearch: string) => {
      if (onApplyFilters) {
        onApplyFilters(newOmnisearch);
      } else {
        setOmnisearch(newOmnisearch);
      }
    },
    [onApplyFilters, setOmnisearch]
  );

  const isFilterOpen = useMemo(() => Boolean(filterButtonAnchor), [filterButtonAnchor]);

  // Helper function to filter out unknown filter keys from an omnisearch string
  const filterValidKeys = useCallback(
    (omnisearchString: string): string => {
      if (!columnFilterKeys || !omnisearchString.trim()) {
        return omnisearchString;
      }

      // Compare on canonical keys so a saved filter serialized to its backend
      // form (e.g. "tenantId"/"facilityId") still matches the column filter key
      // ("tenant"/"facility"). Without this, tenant/facility values were silently
      // dropped from saved filters on restore (ENG-15764).
      const validKeys = new Set(columnFilterKeys.map((key) => getCanonicalKey(key.key)));
      const preservedKeys = new Set(preservedFilterKeys);
      const pairs = extractKeyValuePairs(omnisearchString);

      const filteredPairs = pairs.filter((pair) => {
        const isValid =
          validKeys.has(getCanonicalKey(pair.key)) ||
          (keyAliases[pair.key] && validKeys.has(getCanonicalKey(keyAliases[pair.key]))) ||
          preservedKeys.has(pair.key);
        return isValid;
      });

      let filteredOmnisearch = omnisearchString;
      pairs.forEach((pair) => {
        const originalPairText = omnisearchString.substring(pair.start, pair.end);
        filteredOmnisearch = filteredOmnisearch.replace(originalPairText, "");
      });
      filteredOmnisearch = filteredOmnisearch.replace(/\s+/g, " ").trim();

      const validPairStrings = filteredPairs.map((pair) => {
        const values = pair.value.map((val) => {
          const needsQuotes =
            val.includes(" ") ||
            val.includes(":") ||
            val.includes('"') ||
            val.includes("|") ||
            val.includes(",");
          if (needsQuotes) {
            const escapedValue = val.replace(/"/g, '""');
            return `"${escapedValue}"`;
          }
          return val;
        });
        const valueString = values.length > 1 ? values.join("|") : values[0];
        return `${pair.key}:${valueString}`;
      });

      const result = [filteredOmnisearch, ...validPairStrings]
        .filter((part) => part.trim().length > 0)
        .join(" ")
        .trim();

      return result;
    },
    [columnFilterKeys, keyAliases, preservedFilterKeys]
  );

  // Data grid specific saved filters
  // Note: Key includes user email for per-user filter storage.
  // The key change during user load may trigger effect re-runs, but this
  // should only happen once per page load.
  const savedFiltersKey = persistenceKey
    ? `saved-filters-${persistenceKey}|${currentUser.user?.email}`
    : `saved-filters|${currentUser.user?.email}`;

  const [savedFilters, setSavedFilters] = useLocalStorageState<{
    [name: string]: string;
  }>(savedFiltersKey, { defaultValue: {} });

  const [legacyFavorites] = useLocalStorageState<{
    [favorite: string]: string;
  }>(`filter-favorites|${currentUser.user?.email}`, { defaultValue: {} });

  // Default filter — stores the raw omnisearch value, scoped by tenant + user
  const defaultFilterKey = persistenceKey
    ? `default-filter-${persistenceKey}|${tenantId ?? ""}|${currentUser.user?.email}`
    : `default-filter|${tenantId ?? ""}|${currentUser.user?.email}`;

  const [defaultFilterValue, setDefaultFilterValue] = useLocalStorageState<string | null>(
    defaultFilterKey,
    { defaultValue: null }
  );

  // Auto-apply default filter once the user is loaded and localStorage keys resolve.
  const hasAppliedDefaultRef = useRef(false);
  const prevDefaultFilterKeyRef = useRef(defaultFilterKey);
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const [searchParams] = useSearchParams();
  const hasOmnisearchParam = searchParams.has("omnisearch");

  // Reset when the key changes (tenant switch) so the new tenant's default is applied.
  if (prevDefaultFilterKeyRef.current !== defaultFilterKey) {
    prevDefaultFilterKeyRef.current = defaultFilterKey;
    hasAppliedDefaultRef.current = false;
  }

  // If the Omnisearch stays mounted across route transitions, allow default
  // filters to be re-applied when returning to a page.
  if (prevPathnameRef.current !== location.pathname) {
    prevPathnameRef.current = location.pathname;
    hasAppliedDefaultRef.current = false;
  }

  useEffect(() => {
    if (hasAppliedDefaultRef.current) return;

    // Wait until user email is available (needed for correct localStorage keys)
    if (!currentUser.user?.email) return;

    hasAppliedDefaultRef.current = true;

    // Skip if URL has omnisearch param — explicit URL takes precedence
    if (hasOmnisearchParam) return;

    // Only apply a user-saved default. If none exists, leave whatever
    // useOmnisearchInput already initialized (initialFilters / URL / context)
    // so we don't clobber a parent's default-filter setup.
    if (defaultFilterValue == null) return;

    if (setOmnisearchImmediate) {
      setOmnisearchImmediate(defaultFilterValue);
    } else {
      setOmnisearch(defaultFilterValue);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [
    currentUser.user?.email,
    defaultFilterValue,
    defaultFilterKey,
    location.pathname,
    hasOmnisearchParam,
  ]); // Re-evaluate when localStorage-backed values settle and route context changes

  const savedFiltersList = useMemo(() => {
    const currentFilters = Object.entries(savedFilters).map(([name, query]) => {
      const filteredQuery = filterValidKeys(query);
      return {
        name,
        query: filteredQuery,
        rawQuery: query,
        source: "current" as const,
      };
    });

    const legacyFavoritesFiltered = Object.entries(legacyFavorites)
      .map(([name, query]) => {
        const filteredQuery = filterValidKeys(query);
        return {
          name: `⭐ ${name}`,
          query: filteredQuery,
          rawQuery: query,
          source: "legacy-favorites" as const,
        };
      })
      .filter((item) => {
        const hasValidKeyValuePairs = item.query.includes(":");
        return hasValidKeyValuePairs;
      });

    return [...currentFilters, ...legacyFavoritesFiltered];
  }, [savedFilters, legacyFavorites, filterValidKeys]);

  const handleSavePreset = useCallback(
    (name: string, query: string) => {
      setSavedFilters((prev) => ({
        ...prev,
        [name]: query,
      }));
    },
    [setSavedFilters]
  );

  const handleDeletePreset = useCallback(
    (name: string) => {
      if (name.startsWith("⭐ ")) {
        return;
      }
      setSavedFilters((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    },
    [setSavedFilters]
  );

  const handleSetDefaultFilter = useCallback(
    (value: string | null) => {
      setDefaultFilterValue(value);
    },
    [setDefaultFilterValue]
  );

  // Default external filter button
  const defaultFilterButton = (
    <Button
      variant="outlined"
      size="small"
      onClick={handleOpenFilter}
      startIcon={<FilterList fontSize="small" />}
      sx={{
        whiteSpace: "nowrap",
        minWidth: "auto",
      }}
    >
      Filters
    </Button>
  );

  // Determine if we should show any external filter button
  const shouldShowExternalButton =
    showExternalFilterButton && columnFilterKeys && columnFilterKeys.length > 0;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        width: {
          sm: "80%",
          xs: "100%",
        },
        alignItems: "center",
        ...sx,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <TextField
          autoFocus={autoFocus}
          inputRef={inputRef}
          sx={[
            { flexGrow: 1 },
            ...(Array.isArray(searchFieldSx) ? searchFieldSx : [searchFieldSx]),
          ]}
          name="search"
          value={localInputValue ?? ""}
          onChange={handleInputChange}
          label="Search"
          InputProps={{
            endAdornment: (
              <Box sx={{ display: "flex" }}>
                <IconButton
                  ref={resetRef}
                  sx={{
                    display: localInputValue?.length ? "default" : "none",
                  }}
                  onClick={handleClear}
                  aria-label="clear input"
                >
                  <Clear fontSize="small" />
                </IconButton>
                {columnFilterKeys && showFilterButtonInside && (
                  <Tooltip title="Filters">
                    <IconButton onClick={handleOpenFilter} aria-label="show filters">
                      <Tune fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* External filter button - rendered inside Omnisearch to keep state colocated */}
      {shouldShowExternalButton &&
        (renderFilterButton ? renderFilterButton(handleOpenFilter) : defaultFilterButton)}

      {/* Only render FilterPopover when open to prevent re-renders during typing */}
      {columnFilterKeys && isFilterOpen && (
        <FilterPopover
          isOpen={isFilterOpen}
          filterButtonAnchor={filterButtonAnchor}
          handleCloseFilter={handleCloseFilter}
          columnFilterKeys={columnFilterKeys}
          onApply={onApply}
          omnisearch={omnisearch}
          savedFilters={savedFiltersList}
          onSavePreset={handleSavePreset}
          onDeletePreset={handleDeletePreset}
          onSetDefaultFilter={handleSetDefaultFilter}
          defaultFilterValue={defaultFilterValue}
          filterGroups={filterGroups}
          keyAliases={keyAliases}
          preservedFilterKeys={preservedFilterKeys}
          tenantId={tenantId}
          wideLayout={wideLayout}
        />
      )}
    </Stack>
  );
}
